/**
 * Scroll-driven marquees (additive).
 *
 * For every `.lay-marquee[data-lay-marquee-scroll-drive]` we add scroll
 * velocity (px/sec) × factor on top of the marquee's natural rate. The
 * marquee's configured speed and Hover-Pause / Start-on-Hover behaviour
 * stay intact because we keep track of *our* contribution and subtract
 * it each tick before computing the new one.
 * `data-lay-marquee-scroll-one-direction-only="true"` keeps the scroll
 * contribution moving in the factor's direction for both scroll directions.
 *
 * Lifecycle:
 *   - `marquees_ready` (emitted from marquee.js after every initMarquees)
 *     → refresh pairings in scope (initial load, AJAX nav, project
 *     overlays, page overlays — all funnel through marquees_ready).
 *   - `overlayhidden` ($target) → drop pairings inside the closing page
 *     overlay so the next tick doesn't touch a destroyed instance.
 *   - Per tick: drop pairings whose `.lay-marquee-initialized` class is
 *     gone (defensive cleanup if anything else destroyed the marquee
 *     without us hearing about it).
 */

// Keyed by the `.lay-marquee` DOM element. The element is the only thing
// that survives across re-inits — the LayMarquee instance is replaced.
const pairings = new Map()

let lastTime = typeof performance !== 'undefined' ? performance.now() : 0
let rafId = 0
let running = false

// Walk up to find the nearest scrollable ancestor. Returns `window` if none.
// This is what makes scroll-drive work inside `#lay-project-overlay` and
// page overlays (`.lay-overlay > .lay-content`) — both have their own
// internal scroll, decoupled from `window.scrollY`.
function getDirectLayContentChild(root) {
	if (!root || !root.children) return null
	for (let i = 0; i < root.children.length; i++) {
		const child = root.children[i]
		if (child instanceof HTMLElement && child.classList.contains('lay-content')) return child
	}
	return null
}

function getScrollContainer(el) {
	const pageOverlay = el.closest && el.closest('.lay-overlay')
	if (pageOverlay) {
		const content = getDirectLayContentChild(pageOverlay)
		if (content && content.contains(el)) return content
		return pageOverlay
	}

	const projectOverlay = el.closest && el.closest('#lay-project-overlay')
	if (projectOverlay) return projectOverlay

	let p = el.parentElement
	while (p instanceof HTMLElement) {
		const style = window.getComputedStyle(p)
		const oy = style.overflowY
		if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
			return p
		}
		p = p.parentElement
	}
	return window
}

function getScrollTop(container) {
	return container === window ? window.scrollY : container.scrollTop
}

function refreshPairings($marquees) {
	if (!$marquees || !$marquees.length) return
	$marquees.each(function () {
		const el = this
		if (!el.hasAttribute || !el.hasAttribute('data-lay-marquee-scroll-drive')) return
		const live = jQuery(el).data('lay-marquee')
		// Element might fire `marquees_ready` slightly before its data is
		// attached in some race; just skip — we'll re-pick on next event.
		if (!live) return
		const factor = parseFloat(el.getAttribute('data-lay-marquee-scroll-drive')) || 0
		const existing = pairings.get(el)
		if (existing && existing.marquee !== live) {
			// Re-init: the old instance got destroyed, our previous addition
			// lives there and is gone now. Reset bookkeeping.
			existing.addition = 0
		}
		const container = getScrollContainer(el)
		pairings.set(el, {
			marquee: live,
			factor,
			oneDirectionOnly: el.getAttribute('data-lay-marquee-scroll-one-direction-only') === 'true',
			addition: existing ? existing.addition : 0,
			container,
			lastScrollY: getScrollTop(container),
			smoothedVelocity: 0,
		})
	})
	ensureLoopRunning()
}

function dropPairingsIn($target) {
	if (!$target || !$target.length) return
	const root = $target[0]
	if (!root) return
	for (const [el, pairing] of Array.from(pairings.entries())) {
		if (root === el || (root.contains && root.contains(el))) {
			// Best-effort: subtract our addition so the marquee returns to its
			// natural rate before we forget about it (might already be destroyed).
			try {
				pairing.marquee.setRate(pairing.marquee.getRate() - pairing.addition)
			} catch (e) {}
			pairings.delete(el)
		}
	}
}

function ensureLoopRunning() {
	if (running) return
	if (pairings.size === 0) return
	running = true
	lastTime = performance.now()
	rafId = requestAnimationFrame(tick)
}

function tick() {
	if (pairings.size === 0) {
		running = false
		return
	}

	const now = performance.now()
	const dt = Math.max(1, now - lastTime) // ms; avoid div-by-zero
	lastTime = now

	for (const [el, pairing] of pairings) {
		// Defensive: marquee instances can get destroyed by other paths
		// (CPL toggle, etc.). The class is added/removed by marquee.js.
		if (!el.classList || !el.classList.contains('lay-marquee-initialized')) {
			pairings.delete(el)
			continue
		}
		const live = jQuery(el).data('lay-marquee')
		if (!live) {
			pairings.delete(el)
			continue
		}
		if (live !== pairing.marquee) {
			// Fresh instance after re-init — our previous addition was on the
			// old one and is gone with it. Start fresh.
			pairing.marquee = live
			pairing.addition = 0
		}

		// Per-pairing velocity from this marquee's own scroll container —
		// `window` for normal pages, `#lay-project-overlay` or
		// `.lay-overlay > .lay-content` for overlays.
		const scrollY = getScrollTop(pairing.container)
		const dy = scrollY - pairing.lastScrollY
		pairing.lastScrollY = scrollY
		const rawVelocity = (dy / dt) * 1000 // px / sec
		// EMA smoothing keeps trackpad / inertial bursts from making the
		// marquee snap. Skewed toward responsiveness (0.4 weight on raw).
		pairing.smoothedVelocity = pairing.smoothedVelocity * 0.6 + rawVelocity * 0.4
		const v = Math.abs(pairing.smoothedVelocity) < 0.5 ? 0 : pairing.smoothedVelocity

		const naturalRate = live.getRate() - pairing.addition
		const scrollVelocity = pairing.oneDirectionOnly ? Math.abs(v) : v
		const newAddition = scrollVelocity * pairing.factor
		try {
			live.setRate(naturalRate + newAddition)
			pairing.addition = newAddition
		} catch (e) {
			pairings.delete(el)
		}
	}

	rafId = requestAnimationFrame(tick)
}

const i = function () {
	if (!window.laytheme || typeof window.laytheme.on !== 'function') return

	// Fired by marquee.js after every initMarquees — initial page load,
	// AJAX nav (project overlays), and page-overlay scoped re-inits all
	// funnel through here. Subscribers get the $marquees set + a flag
	// telling them whether the call was page-overlay-scoped.
	window.laytheme.on('marquees_ready', function (detail) {
		if (detail && detail.$marquees) {
			refreshPairings(detail.$marquees)
		}
	})

	// Page overlay closing: marquees inside are about to be destroyed by
	// marquee.js's `overlayhidden` handler. Drop our pairings now so the
	// next rAF tick doesn't poke at stale instances.
	window.laytheme.on('overlayhidden', function ($target) {
		dropPairingsIn($target)
	})
}

const marquee_scroll_drive_controller = { i: i }
export default marquee_scroll_drive_controller
